import { useEffect, useCallback, useState } from 'react';
import { Globe, Lock, Upload, Users } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';

import { useTranslation } from '../../i18n';
import useFormValidation from '../../hooks/useFormValidation';
import {
  createSpaceSchema,
  ICreateSpaceFormData,
} from '../../schemas/spaceSchemas';
import {
  useCreateSpace,
  useUpdateSpace,
  spaceKeys,
} from '../../hooks/queries/useSpaceQueries';
import { api } from '../../services/client';
import { ISpace, ISpaceOwner } from '../../types';
import { getImageUrl } from '../../utils/imageUrl';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import UserAutocomplete from '../UserAutocomplete';

const INITIAL_VALUES: ICreateSpaceFormData = {
  name: '',
  description: '',
  visibility: 'private',
  ownerId: '',
  joinRule: '',
  categoryId: '',
  rules: '',
};

interface ISpaceFormDialogProps {
  isOpen: boolean;
  onClose: () => void;
  space?: ISpace | null;
}

const FieldError = ({ message }: { message?: string }) =>
  message ? <p className="text-xs text-red-500 mt-1">{message}</p> : null;

const SpaceFormDialog = ({ isOpen, onClose, space }: ISpaceFormDialogProps) => {
  const { t } = useTranslation();
  const isEdit = !!space;
  const queryClient = useQueryClient();
  const { values, errors, handleChange, handleSubmit, reset, isSubmitting } =
    useFormValidation(createSpaceSchema, INITIAL_VALUES);
  const createSpace = useCreateSpace();
  const updateSpace = useUpdateSpace();
  const [avatarPreview, setAvatarPreview] = useState<string>('');
  const [avatarFile, setAvatarFile] = useState<File | null>(null);

  useEffect(() => {
    if (isOpen && space) {
      reset({
        name: space.name,
        description: space.description || '',
        visibility: (space.visibility as 'public' | 'private') || 'public',
        ownerId: space.ownerId || space.owner?.id || '',
        joinRule: space.joinRule || '',
        rules: space.rules || '',
        categoryId: space.categoryId || '',
      });
      setAvatarPreview('');
      setAvatarFile(null);
    } else if (isOpen) {
      reset();
      setAvatarPreview('');
      setAvatarFile(null);
    }
  }, [isOpen, space]);

  const fetchUsers = useCallback(async (search?: string) => {
    const result = await api.get<ISpaceOwner[]>(
      `/backoffice/space-owners${search ? `?search=${search}` : ''}`,
    );

    return result.map(({ id, username, displayName, avatar }) => ({
      id,
      username,
      displayName,
      avatar,
    }));
  }, []);

  const handleFormSubmit = async () => {
    await handleSubmit(async (data) => {
      if (isEdit) {
        await updateSpace.mutateAsync({
          id: space!.id,
          data: {
            name: data.name,
            description: data.description,
            visibility: data.visibility,
            joinRule: data.joinRule || undefined,
            rules: data.rules || undefined,
          },
        });

        if (avatarFile) {
          try {
            await api.upload(
              `/backoffice/spaces/${space!.id}/upload-avatar`,
              avatarFile,
            );
            queryClient.invalidateQueries({ queryKey: spaceKeys.lists() });
            queryClient.invalidateQueries({
              queryKey: spaceKeys.detail(space!.id),
            });
          } catch {
            // Space updated, avatar upload failed silently
          }
        }
      } else {
        const result = await createSpace.mutateAsync({
          name: data.name,
          description: data.description,
          visibility: data.visibility,
          ownerId: data.ownerId,
          joinRule: data.joinRule || undefined,
        });

        if (avatarFile && result && (result as any).id) {
          try {
            await api.upload(
              `/backoffice/spaces/${(result as any).id}/upload-avatar`,
              avatarFile,
            );
            queryClient.invalidateQueries({ queryKey: spaceKeys.lists() });
            queryClient.invalidateQueries({
              queryKey: spaceKeys.detail((result as any).id),
            });
          } catch {
            // Space created, avatar upload failed silently
          }
        }
      }
      onClose();
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="sm:max-w-[520px]"
        data-testid="SpaceManagement_List_spaceFormModal"
      >
        <DialogHeader>
          <DialogTitle>
            {isEdit ? t('spaces.edit_space') : t('spaces.create_space')}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? t('spaces.edit_space_desc')
              : t('spaces.create_space_desc')}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {/* Avatar */}
          <div>
            <label className="block text-sm font-semibold text-foreground mb-2">
              {t('space_settings.avatar')}
            </label>
            <div className="flex items-center gap-3">
              <div
                className="w-14 h-14 rounded-xl flex items-center justify-center overflow-hidden shrink-0"
                style={{
                  background:
                    avatarPreview || (isEdit && space?.avatar)
                      ? 'transparent'
                      : 'linear-gradient(135deg, #3B82F6, #A855F7)',
                }}
              >
                {avatarPreview ? (
                  <img
                    src={avatarPreview}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                ) : isEdit && space?.avatar ? (
                  <img
                    src={getImageUrl(space.avatar)!}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <Users className="w-6 h-6 text-white" />
                )}
              </div>
              <div className="flex items-center gap-2">
                <label className="cursor-pointer">
                  <Button
                    variant="outline"
                    size="sm"
                    asChild
                    data-testid="SpaceManagement_List_spaceFormModalUploadAvatarButton"
                  >
                    <span className="flex items-center gap-1.5">
                      <Upload className="w-3.5 h-3.5" />
                      {t('space_settings.upload_avatar')}
                    </span>
                  </Button>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];

                      if (file) {
                        setAvatarFile(file);
                        setAvatarPreview(URL.createObjectURL(file));
                      }
                      e.target.value = '';
                    }}
                  />
                </label>
                {(avatarPreview || (isEdit && space?.avatar)) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setAvatarFile(null);
                      setAvatarPreview('');
                    }}
                    className="text-xs text-red-500 hover:text-red-700"
                    data-testid="SpaceManagement_List_spaceFormModalRemoveAvatarButton"
                  >
                    {t('common.remove')}
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Name */}
          <div>
            <label className="block text-sm font-semibold text-foreground mb-1">
              {t('spaces.name_required')}
            </label>
            <Input
              value={values.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder={t('spaces.space_name_placeholder')}
              data-testid="SpaceManagement_List_spaceFormModalNameInput"
            />
            <p className="text-xs text-muted-foreground mt-1">
              {t('spaces.name_must_be_unique')}
            </p>
            <FieldError message={errors.name} />
          </div>

          {/* Owner — create mode only */}
          {!isEdit && (
            <div>
              <label className="block text-sm font-semibold text-foreground mb-1">
                {t('spaces.space_owner')}
              </label>
              <UserAutocomplete
                value={values.ownerId}
                onChange={(userId) => handleChange('ownerId', userId)}
                fetchUsers={fetchUsers}
                placeholder={t('spaces.search_users')}
                triggerPlaceholder={t('spaces.select_owner')}
                testId="SpaceManagement_List_spaceFormModalOwnerSelect"
              />
              <p className="text-xs text-muted-foreground mt-1">
                {t('spaces.owner_must_select_existing')}
              </p>
              <FieldError message={errors.ownerId} />
            </div>
          )}

          {/* Description */}
          <div>
            <label className="block text-sm font-semibold text-foreground mb-1">
              {t('spaces.description_label')}
            </label>
            <Textarea
              value={values.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder={t('spaces.space_desc_placeholder')}
              rows={3}
              data-testid="SpaceManagement_List_spaceFormModalDescriptionTextArea"
            />
            <FieldError message={errors.description} />
          </div>

          {/* Visibility */}
          <div>
            <label className="block text-sm font-semibold text-foreground mb-1">
              {t('spaces.visibility')}
            </label>
            <Select
              value={values.visibility}
              onValueChange={(val) => handleChange('visibility', val)}
            >
              <SelectTrigger
                className="w-full h-auto py-2.5"
                data-testid="SpaceManagement_List_spaceFormModalVisibilitySelect"
              >
                <SelectValue>
                  <div className="flex items-center gap-2">
                    {values.visibility === 'public' ? (
                      <Globe className="w-4 h-4 text-muted-foreground shrink-0" />
                    ) : (
                      <Lock className="w-4 h-4 text-muted-foreground shrink-0" />
                    )}
                    <div className="text-left">
                      <div className="text-sm font-medium">
                        {values.visibility === 'public'
                          ? t('spaces.type_public')
                          : t('spaces.type_private')}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {values.visibility === 'public'
                          ? t('spaces.visibility_public_desc')
                          : t('spaces.visibility_private_desc')}
                      </div>
                    </div>
                  </div>
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem
                  value="public"
                  className="py-2.5"
                  data-testid="SpaceManagement_List_spaceFormModalVisibilitySelectOption_public"
                >
                  <div className="flex items-center gap-2">
                    <Globe className="w-4 h-4 text-muted-foreground shrink-0" />
                    <div>
                      <div className="text-sm font-medium">
                        {t('spaces.type_public')}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {t('spaces.visibility_public_desc')}
                      </div>
                    </div>
                  </div>
                </SelectItem>
                <SelectItem
                  value="private"
                  className="py-2.5"
                  data-testid="SpaceManagement_List_spaceFormModalVisibilitySelectOption_private"
                >
                  <div className="flex items-center gap-2">
                    <Lock className="w-4 h-4 text-muted-foreground shrink-0" />
                    <div>
                      <div className="text-sm font-medium">
                        {t('spaces.type_private')}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {t('spaces.visibility_private_desc')}
                      </div>
                    </div>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
            <FieldError message={errors.visibility} />
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            data-testid="SpaceManagement_List_spaceFormModalCancelButton"
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleFormSubmit}
            disabled={isSubmitting}
            data-testid="SpaceManagement_List_spaceFormModalSubmitButton"
          >
            {isSubmitting
              ? t('common.saving')
              : isEdit
                ? t('spaces.save_changes')
                : t('common.create')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SpaceFormDialog;
